import React from 'react';
import { ArrowLeft, ArrowRight, Menu } from 'lucide-react';
import type { ToolStep } from './types';

export interface ToolToolbarProps {
  step: ToolStep;
  stepIndex: number;
  total: number;
  onAdvance?: () => void;
  onBack?: () => void;
  canGoBack: boolean;
  /** Override "Next" label, e.g. "Finish" on last step or a tool-specific verb. */
  advanceLabel?: string;
  /** Disable Next while the current step isn't ready. */
  advanceDisabled?: boolean;
  /** Below `lg` the sidebar is a drawer. The toolbar gets a hamburger that
   *  toggles it; the parent ToolShell handles the open/closed state. */
  onOpenDrawer?: () => void;
  /** Hide the primary Next button — terminal steps (e.g. Download) own their
   *  own actions inside the workspace. Defaults to true. */
  showAdvance?: boolean;
}

/** Slim dark utility bar — step counter, step name, blurb, Back / Next pills.
 *  Sticky to the top of the workspace.
 *
 *  At md and below: leads with a hamburger to open the sidebar drawer.
 *  Hit targets: pills bump to 44px tall below sm per mobile rules. */
export const ToolToolbar: React.FC<ToolToolbarProps> = ({
  step,
  stepIndex,
  total,
  onAdvance,
  onBack,
  canGoBack,
  advanceLabel,
  advanceDisabled,
  onOpenDrawer,
  showAdvance = true,
}) => {
  const isLast = stepIndex === total - 1;
  const nextLabel = advanceLabel ?? (isLast ? 'Finish' : 'Next');

  return (
    <div
      className="flex items-center sticky top-0 z-10 gap-2 sm:gap-4 px-4 sm:px-6 lg:px-7 py-3"
      style={{
        borderBottom: '1px solid var(--hj-hairline-soft)',
        background: 'var(--hj-surface-1)',
      }}
    >
      {/* Drawer toggle — only visible below lg. 44px hit target. */}
      {onOpenDrawer && (
        <button
          type="button"
          onClick={onOpenDrawer}
          aria-label="Open pipeline steps"
          className="lg:hidden inline-flex items-center justify-center flex-shrink-0"
          style={{
            width: 44,
            height: 44,
            margin: '-8px -8px -8px -10px',
            background: 'transparent',
            border: 0,
            color: 'var(--hj-ink-2)',
            cursor: 'pointer',
          }}
        >
          <Menu size={20} strokeWidth={1.8} />
        </button>
      )}

      <div className="flex items-baseline min-w-0 flex-1 gap-2 sm:gap-3">
        <span
          className="tabular text-[10px] hidden sm:inline"
          style={{
            fontFamily: 'var(--hj-font-mono)',
            color: 'var(--hj-ink-4)',
            letterSpacing: '0.08em',
          }}
        >
          STEP {String(stepIndex + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </span>
        <span
          className="whitespace-nowrap text-[14px] font-semibold"
          style={{ color: 'var(--hj-ink)' }}
        >
          {step.label}
        </span>
        {step.blurb && (
          <>
            <span className="hidden md:inline" style={{ color: 'var(--hj-ink-4)' }}>·</span>
            <span
              className="truncate text-[13px] min-w-0 hidden md:inline"
              style={{ color: 'var(--hj-ink-2)' }}
            >
              {step.blurb}
            </span>
          </>
        )}
      </div>

      {/* Back/Next pills are md+ only. At sm the MobileActionBar at the
          bottom of the workspace owns the primary CTA per designer rules. */}
      <div className="hidden md:flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={onBack}
          disabled={!canGoBack}
          className="inline-flex items-center gap-1 rounded-full text-[13px] font-medium transition-opacity disabled:cursor-not-allowed px-3 py-1.5"
          style={{
            background: 'transparent',
            border: '1px solid var(--hj-hairline)',
            color: 'var(--hj-ink-2)',
            opacity: canGoBack ? 1 : 0.4,
            cursor: canGoBack ? 'pointer' : 'not-allowed',
          }}
        >
          <ArrowLeft size={14} strokeWidth={2} />
          Back
        </button>
        {showAdvance && (
          <button
            type="button"
            onClick={onAdvance}
            disabled={advanceDisabled}
            className="inline-flex items-center gap-1 rounded-full text-[13px] font-semibold transition-opacity disabled:cursor-not-allowed px-4 py-1.5"
            style={{
              background: 'var(--hj-blue)',
              border: '1px solid var(--hj-blue)',
              color: 'oklch(0.18 0.005 70)',
              opacity: advanceDisabled ? 0.5 : 1,
              cursor: advanceDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            {nextLabel}
            <ArrowRight size={14} strokeWidth={2} />
          </button>
        )}
      </div>
    </div>
  );
};